import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { changePasswordInputSchema, MIN_PASSWORD_LENGTH } from 'used-vehicle-contracts';
import { isUnauthorizedError } from '../../apis/orpc-client';
import { useAuth } from '../../auth/AuthContext';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { useI18nTranslation } from '../../lib/use-i18n-translation';

/** The confirmation field never leaves the browser, so it is a view-only extension of the contract input. */
const formSchema = changePasswordInputSchema
  .extend({ confirmPassword: z.string().min(1) })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'The two passwords do not match',
  });

type FormValues = z.infer<typeof formSchema>;

/**
 * Reached in two ways: from the user footer at any time, and forced by `ProtectedRoute` while
 * `mustChangePassword` is set after an administrator's reset (plan §2.3).
 */
export const ChangePasswordPage = () => {
  const { t } = useI18nTranslation();
  const { user, mustChangePassword, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
      />
    );
  }

  const onSubmit = handleSubmit(async ({ currentPassword, newPassword }) => {
    setSubmitError(null);

    try {
      await changePassword({ currentPassword, newPassword });
      navigate('/dashboard', { replace: true });
    } catch (error) {
      setSubmitError(isUnauthorizedError(error) ? t('changePassword.wrongCurrent') : t('errors.saveFailed'));
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <p className="text-lg font-semibold tracking-tight text-foreground">{t('app.name')}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{user.tenantName}</p>
        </div>

        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          <h1 className="text-xl font-semibold text-card-foreground">{t('changePassword.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {mustChangePassword ? t('changePassword.forcedSubtitle') : t('changePassword.subtitle')}
          </p>

          <form
            onSubmit={onSubmit}
            noValidate
            className="mt-6 space-y-4"
          >
            <FormField
              id="currentPassword"
              label={t('changePassword.current')}
              error={errors.currentPassword?.message}
            >
              <Input
                id="currentPassword"
                type="password"
                autoComplete="current-password"
                autoFocus
                dir="ltr"
                className="text-start"
                hasError={Boolean(errors.currentPassword)}
                {...register('currentPassword')}
              />
            </FormField>

            <FormField
              id="newPassword"
              label={t('changePassword.new')}
              hint={t('changePassword.hint', { min: MIN_PASSWORD_LENGTH })}
              error={errors.newPassword?.message}
            >
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                dir="ltr"
                className="text-start"
                hasError={Boolean(errors.newPassword)}
                {...register('newPassword')}
              />
            </FormField>

            <FormField
              id="confirmPassword"
              label={t('changePassword.confirm')}
              error={errors.confirmPassword ? t('changePassword.mismatch') : undefined}
            >
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                dir="ltr"
                className="text-start"
                hasError={Boolean(errors.confirmPassword)}
                {...register('confirmPassword')}
              />
            </FormField>

            {submitError ? (
              <p
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
              >
                {submitError}
              </p>
            ) : null}

            <Button
              type="submit"
              isLoading={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? t('actions.saving') : t('changePassword.submit')}
            </Button>
          </form>
        </div>

        <div className="mt-4 flex items-center justify-center gap-2">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={logout}
            className="inline-flex h-8 items-center rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {t('actions.signOut')}
          </button>
        </div>
      </div>
    </div>
  );
};
